import type { Brand as MelwaysBrand } from '@seek/melways-sites';

// Define a local Brand type that includes seekAnz if it's missing
type Brand = MelwaysBrand | 'seekAnz';

import { TRACKING_CODES } from 'src/shared/constant';
import type { SuggestionType } from 'src/shared/types';
import { getCorrectlyCasedBrandName } from 'src/utils/brandUtils';

import {
  suggestionMapping,
  type UrlWithPlaceholder,
  type Region,
} from './suggestionMapping';

const mapBrandToProfileKey = (brand: Brand): string => {
  // Add specific checks for seekAnz if needed, or map it to 'seek'
  const normalizedBrand = brand === 'seekAnz' ? 'seek' : brand;
  switch (normalizedBrand) {
    case 'seek':
      return 'SEEK Profile';
    // Add other brand mappings if needed
    default:
      return 'Jobstreet Profile'; // Default or Jobstreet/JobsDB
  }
};

export const createSuggestion = (
  subject: string,
  reason: string,
  countryCode: string,
  brand: Brand,
): SuggestionType | undefined => {
  const upperCaseCountryCode = countryCode.toUpperCase() as Region;
  // Normalize brand first
  const normalizedBrandForCasing = brand === 'seekAnz' ? 'seek' : brand;
  const correctlyCasedBrand = getCorrectlyCasedBrandName(
    normalizedBrandForCasing,
  );

  // Use explicit brand profile key if the subject is related to profile
  let subjectKeyToLookup = subject;
  if (subject.toLowerCase().includes('profile')) {
    subjectKeyToLookup = mapBrandToProfileKey(brand);
  }

  const subjectMapping = suggestionMapping[subjectKeyToLookup];
  if (!subjectMapping) {
    // console.error('[createSuggestion] Subject mapping NOT FOUND for key:', subjectKeyToLookup);
    return undefined;
  }

  const reasonMapping = subjectMapping[reason];
  if (!reasonMapping) {
    // console.error('[createSuggestion] Reason mapping NOT FOUND for key:', reason, 'within subject:', subjectKeyToLookup);
    return undefined;
  }

  const urls = reasonMapping.urls?.[upperCaseCountryCode];
  if (!urls && reasonMapping.urls) {
    // Check if urls object exists but country code is missing
    // console.error('[createSuggestion] URLs NOT FOUND for countryCode:', upperCaseCountryCode, 'within reason:', reason);
    // Potentially return undefined or handle differently if URLs are mandatory
  }

  const variables: Record<string, any> = {};
  let finalMessage = reasonMapping.message;

  // Replace %BrandName% placeholder
  finalMessage = finalMessage.replace(/%BrandName%/g, correctlyCasedBrand);

  // Process URLs
  if (urls) {
    if (Array.isArray(urls)) {
      // Handle array of UrlWithPlaceholder
      urls.forEach((urlObj: UrlWithPlaceholder) => {
        variables[urlObj.placeholder] = {
          label: urlObj.label,
          href: urlObj.href,
          brand,
          tracking: TRACKING_CODES.can_help_centre_link_clicked,
        };
        // Also replace placeholders in the message string if they match
        finalMessage = finalMessage.replace(
          `%${urlObj.placeholder}%`,
          urlObj.label, // Or potentially create a link here if desired
        );
      });
    } else if (typeof urls === 'string') {
      // Handle single URL string - assume %HelpCentre placeholder
      variables.HelpCentre = {
        label: 'Help Centre',
        href: urls,
        brand,
        tracking: TRACKING_CODES.can_help_centre_link_clicked,
      };
      // Replace %HelpCentre% in the message string
      finalMessage = finalMessage.replace(
        /%HelpCentre%/g,
        'Help Centre', // Or potentially create a link here
      );
    }
  } else {
    // Handle cases where there are no URLs but the message might still have placeholders
    // For example, %HelpCentre% might be used generically even without a specific URL
    finalMessage = finalMessage.replace(/%HelpCentre%/g, 'Help Centre');
  }

  return {
    message: finalMessage,
    variables,
  };
};
